import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, XCircle } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { money } from '@/lib/money'
import type { Order, RefundOption, RefundPreview } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'
import { invalidateOrderViews } from '@/components/order/lifecycle'

const choices: { value: RefundOption; label: string; hint: (preview: RefundPreview) => string }[] = [
  {
    value: 'wallet',
    label: "Back to the customer's wallet",
    hint: (p) => `Credited now, up to ${money(p.wallet_refundable)} - what the wallet paid for this order.`,
  },
  {
    value: 'manual',
    label: 'Refund by hand',
    hint: () =>
      'Puts it on the refund list. Return the money through the payment gateway or a bank transfer, then record it on this order.',
  },
  {
    value: 'none',
    label: 'Keep the payment',
    hint: () => 'No refund. The reason stays in the order history.',
  },
]

/**
 * Cancelling an order. The refund choices come from the server's preview of
 * the order's payment, and the server decides again from the locked row when
 * the cancel lands - if the payment changed in between, it wins.
 */
export default function CancelOrderModal({
  order,
  refund,
  onClose,
}: {
  order: Order | null
  refund?: RefundPreview | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [method, setMethod] = useState<RefundOption | ''>('')
  const [amount, setAmount] = useState('')

  useEffect(() => {
    if (!order) return
    setReason('')
    setMethod(refund?.default ?? '')
    setAmount(refund && refund.wallet_refundable > 0 ? String(refund.wallet_refundable) : '')
    // Reset only when the dialog opens on an order, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id])

  const amountValue = Number(amount)
  const amountOk = method !== 'wallet' || (amountValue > 0 && amountValue <= (refund?.wallet_refundable ?? 0))
  const ready = reason.trim().length > 0 && (!refund?.money_taken || method !== '') && amountOk

  const cancel = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ message?: string }>(`/admin/orders/${order!.id}/cancel`, {
          reason: reason.trim(),
          refund: refund?.money_taken ? method : null,
          amount: method === 'wallet' ? amountValue : null,
        })
      ).data,
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      queryClient.invalidateQueries({ queryKey: ['capacity'] })
      onClose()
      toast.success('Order cancelled', res.message)
    },
    onError: (err) => toast.error('Could not cancel the order', errorMessage(err)),
  })

  return (
    <Modal
      open={Boolean(order)}
      onClose={onClose}
      title={order ? `Cancel ${order.number}?` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep order
          </Button>
          <Button variant="danger" disabled={!ready} loading={cancel.isPending} onClick={() => cancel.mutate()}>
            <XCircle className="size-4" /> Cancel order
          </Button>
        </>
      }
    >
      {order && refund && (
        <div className="space-y-4">
          <p className="flex gap-2 rounded-lg border border-danger/60 bg-danger/20 p-3 text-sm text-danger">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            The job leaves the floor at once, and its section slots and production quota are freed. This cannot be
            undone.
          </p>
          <Field label="Reason" htmlFor="cancel-reason">
            <Textarea
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Customer changed their mind"
            />
          </Field>

          {refund.money_taken ? (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm text-foreground">
                The customer paid {money(refund.total)}. How does it go back?
              </legend>
              {choices
                .filter((choice) => refund.options.includes(choice.value))
                .map((choice) => (
                  <label
                    key={choice.value}
                    className={cn(
                      'flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-sm',
                      method === choice.value ? 'border-primary bg-primary/5' : 'border-line',
                    )}
                  >
                    <input
                      type="radio"
                      name="refund"
                      checked={method === choice.value}
                      onChange={() => setMethod(choice.value)}
                      className="mt-0.5 size-4 accent-primary"
                    />
                    <span>
                      <span className="font-semibold">{choice.label}</span>
                      <span className="block text-xs text-muted">{choice.hint(refund)}</span>
                    </span>
                  </label>
                ))}
              {method === 'wallet' && (
                <Field
                  label="Amount back to the wallet (RM)"
                  htmlFor="cancel-amount"
                  error={amountOk ? undefined : `Between RM0.01 and ${money(refund.wallet_refundable)}.`}
                >
                  <Input
                    id="cancel-amount"
                    type="number"
                    min={0.01}
                    step="0.01"
                    max={refund.wallet_refundable}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Field>
              )}
            </fieldset>
          ) : (
            <p className="rounded-lg bg-app p-2.5 text-xs text-muted">
              {order.payment_status === 'pending'
                ? 'The online payment was not completed, so nothing is refunded. If it still arrives, the order stays cancelled and a refund is flagged for you.'
                : 'No payment was taken, so nothing is refunded.'}
            </p>
          )}

          {refund.customer_has_email ? (
            <p className="text-xs text-muted">The customer is emailed that the order was cancelled.</p>
          ) : (
            <p className="text-xs font-semibold text-warning">
              This customer has no email address, so nobody tells them automatically. Call them.
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
